import { Pressable, View } from 'react-native'
import Animated, {
  interpolate,
  useAnimatedStyle,
  useSharedValue,
} from 'react-native-reanimated'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import useAnimatedToggle from '@ui/hooks/useAnimatedToggle'
import Typography from '@ui/typography'
import Icon from '@ui/elements/Icon'
import { IconName } from '@ui/elements/Icon'
import Status from '@ui/elements/Status'
import { ToggleIcon } from '@ui/forms/Select/ToggleIcon'
import { colors } from '@ui/colors'
import { PaymentMethod, PaymentMethodAvailability } from '@ca/common'
import { resolveProvider } from '@order/hooks/usePaymentMethods'
import usePaymentTranslations from '@order/hooks/usePaymentTranslations'
import DisableReasonItem from './DisableReasonItem'

const METHOD_ICONS: Record<PaymentMethod, IconName> = {
  terminal: 'credit-card',
  paylink: 'world-dollar',
  monthly: 'calendar-dollar',
  invoice: 'building-bank',
}

export default function MethodInfoCard({
  item,
  hasSelection,
}: {
  item: PaymentMethodAvailability
  hasSelection: boolean
}) {
  const { t } = useTranslation(['ui', 'types', 'payment', 'modals'])
  const [expanded, setExpanded] = useState(false)

  const provider = resolveProvider(item.method)
  const { key } = usePaymentTranslations({
    method: item.method,
    provider,
  })

  const title = t(`types:payment.method.${item.method}`)
  const desc = t(`payment:settings.method.options.${key}.help`)
  const icon = METHOD_ICONS[item.method]

  const collapsedHeight = useSharedValue(0)
  const fullHeight = useSharedValue(0)
  const animation = useAnimatedToggle(expanded, {
    spring: { mass: 1, damping: 100, stiffness: 100 },
  })

  const containerStyle = useAnimatedStyle(() => {
    if (collapsedHeight.value === 0 || fullHeight.value === 0) return {}
    return {
      height: interpolate(
        animation.value,
        [0, 1],
        [collapsedHeight.value, fullHeight.value]
      ),
    }
  })

  const toggle = useCallback(() => {
    setExpanded((v) => !v)
  }, [])

  return (
    <View className="flex flex-col rounded-2xl border border-black/10 px-8 py-6">
      <View className="flex flex-row gap-6">
        <View className="pt-0.5">
          <Icon name={icon} size={24} color={colors.text} />
        </View>

        <View className="flex flex-1 flex-col gap-2">
          <View className="flex flex-row items-center justify-between">
            <Typography variant="MDB" color={colors.text}>
              {title}
            </Typography>
          </View>

          <Pressable onPress={toggle}>
            <View className="relative w-full">
              <View
                className="absolute left-0 right-0 opacity-0"
                pointerEvents="none"
                onLayout={(e) => {
                  collapsedHeight.set(e.nativeEvent.layout.height)
                }}
              >
                <Typography
                  variant="SM"
                  color={colors.neutral[400]}
                  numberOfLines={2}
                >
                  {desc}
                </Typography>
              </View>

              <View
                className="absolute left-0 right-0 opacity-0"
                pointerEvents="none"
                onLayout={(e) => {
                  fullHeight.set(e.nativeEvent.layout.height)
                }}
              >
                <Typography variant="SM" color={colors.neutral[400]}>
                  {desc}
                </Typography>
              </View>

              <Animated.View style={containerStyle} className="overflow-hidden">
                <Typography variant="SM" color={colors.neutral[400]}>
                  {desc}
                </Typography>
              </Animated.View>

              <View
                className="absolute bottom-0 right-0"
                pointerEvents="none"
              >
                <ToggleIcon
                  animation={animation}
                  size={16}
                  color={colors.neutral[400]}
                  className="items-center justify-center pb-0.5"
                />
              </View>
            </View>
          </Pressable>

          {!item.available && item.disableReasons.length > 0 && (
            <View className="mt-1 flex flex-col gap-2">
              {item.disableReasons.map((reason) => (
                <DisableReasonItem
                  key={reason}
                  reason={reason}
                  method={item.method}
                />
              ))}
            </View>
          )}
        </View>
        <View className="flex">
          <Status
            text={
              hasSelection
                ? t(item.available ? 'ui:available' : 'ui:unavailable')
                : t('ui:selectOrder')
            }
            variant={
              hasSelection
                ? item.available
                  ? 'active'
                  : 'neutral'
                : 'neutral'
            }
            colors={
              !hasSelection
                ? { front: colors.text, back: colors.gray[75] }
                : undefined
            }
            size="small"
          />
        </View>
      </View>
    </View>
  )
}
